import * as THREE from "three";
import { FIELD_LAYOUTS, ROAD_LAYOUTS } from "./layout";

const textureLoader = new THREE.TextureLoader();

function loadTexture(path: string, color = false, repeatX = 1, repeatY = 1): THREE.Texture {
  if (typeof document === "undefined") return new THREE.Texture();
  const texture = textureLoader.load(path);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  if (color) texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

const GROUND_COLOR = loadTexture("/textures/world/Ground102_1K-JPG_Color.jpg", true, 22, 22);
const GROUND_NORMAL = loadTexture("/textures/world/Ground102_1K-JPG_NormalGL.jpg", false, 22, 22);
const GROUND_ROUGHNESS = loadTexture("/textures/world/Ground102_1K-JPG_Roughness.jpg", false, 22, 22);

const DRY_COLOR = loadTexture("/textures/world/Gravel010_1K-JPG_Color.jpg", true, 26, 26);
const DRY_NORMAL = loadTexture("/textures/world/Gravel010_1K-JPG_NormalGL.jpg", false, 26, 26);
const DRY_ROUGHNESS = loadTexture("/textures/world/Gravel010_1K-JPG_Roughness.jpg", false, 26, 26);

const GRASS_COLOR = loadTexture("/textures/ground/aerial_grass_rock/aerial_grass_rock_diff_1k.jpg", true);
const ROCK_COLOR = loadTexture("/textures/ground/rocky_terrain_02/rocky_terrain_02_diff_1k.jpg", true);

export type SplatMaterialConfig = {
  base: {
    map: THREE.Texture;
    normalMap: THREE.Texture;
    roughnessMap: THREE.Texture;
    normalScale?: number;
  };
  // Diffuse blended in by the `splat` vertex attribute: x → splatX, y → splatY,
  // z → base. World-space UV scales per layer.
  splatX: { map: THREE.Texture; scale: number };
  splatY: { map: THREE.Texture; scale: number };
};

// Splat-blended ground: the base MeshStandardMaterial keeps its map/normal/
// roughness (full lighting/shadow support); a shader patch blends two more
// diffuse layers using the per-vertex `splat` weights written by the map's
// paint pass, with a two-scale resample to break tiling at altitude. Vertex
// colors remain a tint layer carrying roads/wear/wetness variation.
export function makeSplatGroundMaterial(cfg: SplatMaterialConfig): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    map: cfg.base.map,
    normalMap: cfg.base.normalMap,
    roughnessMap: cfg.base.roughnessMap,
    roughness: 1,
    metalness: 0,
    normalScale: new THREE.Vector2(cfg.base.normalScale ?? 0.28, cfg.base.normalScale ?? 0.28),
  });

  mat.onBeforeCompile = (shader) => {
    shader.uniforms["grassMap"] = { value: cfg.splatX.map };
    shader.uniforms["rockMap"] = { value: cfg.splatY.map };
    shader.uniforms["splatXScale"] = { value: cfg.splatX.scale };
    shader.uniforms["splatYScale"] = { value: cfg.splatY.scale };

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
        attribute vec3 splat;
        varying vec3 vSplatW;
        varying vec3 vSplatWorld;`,
      )
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        vSplatW = splat;
        vSplatWorld = (modelMatrix * vec4(position, 1.0)).xyz;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
        uniform sampler2D grassMap;
        uniform sampler2D rockMap;
        uniform float splatXScale;
        uniform float splatYScale;
        varying vec3 vSplatW;
        varying vec3 vSplatWorld;

        float splatHash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }
        float splatNoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(splatHash(i), splatHash(i + vec2(1.0, 0.0)), u.x),
            mix(splatHash(i + vec2(0.0, 1.0)), splatHash(i + vec2(1.0, 1.0)), u.x),
            u.y
          );
        }`,
      )
      .replace(
        "#include <map_fragment>",
        `{
          // Macro noise drives the anti-tiling resample blend
          float macroN = splatNoise(vSplatWorld.xz * 0.006) * 0.65
                       + splatNoise(vSplatWorld.xz * 0.023) * 0.35;

          vec4 dirtTex = texture2D(map, vMapUv);
          dirtTex = mix(dirtTex, texture2D(map, vMapUv * 0.27 + 3.7), macroN * 0.5);

          vec2 grassUv = vSplatWorld.xz * splatXScale;
          vec4 grassTex = texture2D(grassMap, grassUv);
          grassTex = mix(grassTex, texture2D(grassMap, grassUv * 0.31 + 17.0), macroN * 0.55);

          vec2 rockUv = vSplatWorld.xz * splatYScale;
          vec4 rockTex = texture2D(rockMap, rockUv);
          rockTex = mix(rockTex, texture2D(rockMap, rockUv * 0.29 + 9.0), macroN * 0.5);

          vec3 w = vSplatW / max(vSplatW.x + vSplatW.y + vSplatW.z, 0.001);
          diffuseColor *= grassTex * w.x + rockTex * w.y + dirtTex * w.z;
        }`,
      );
  };

  return mat;
}

export const TERRAIN_MATERIALS = {
  ground: makeSplatGroundMaterial({
    base: { map: GROUND_COLOR, normalMap: GROUND_NORMAL, roughnessMap: GROUND_ROUGHNESS },
    splatX: { map: GRASS_COLOR, scale: 0.055 },
    splatY: { map: ROCK_COLOR, scale: 0.045 },
  }),
  urban: new THREE.MeshStandardMaterial({ color: 0x3c3c3c, roughness: 0.7, emissive: new THREE.Color(0x111111) }),
  roof: new THREE.MeshStandardMaterial({ color: 0x4a4040, roughness: 0.65, emissive: new THREE.Color(0x120a08), emissiveIntensity: 0.5 }),
  roofMetal: new THREE.MeshStandardMaterial({ color: 0x505860, roughness: 0.25, metalness: 0.6, emissive: new THREE.Color(0x08101a), emissiveIntensity: 0.7 }),
  suburban: new THREE.MeshStandardMaterial({ color: 0x383530, roughness: 0.8, emissive: new THREE.Color(0x0a0a08), emissiveIntensity: 0.35 }),
  rural: new THREE.MeshStandardMaterial({ color: 0x2e2c1e, roughness: 0.9, emissive: new THREE.Color(0x08080a), emissiveIntensity: 0.2 }),
  compound: new THREE.MeshStandardMaterial({ color: 0x3a3020, roughness: 0.75, emissive: new THREE.Color(0x0c0a04), emissiveIntensity: 0.3 }),
  water: new THREE.MeshStandardMaterial({ color: 0x0a1820, roughness: 0.05, metalness: 0.4 }),
  farField: new THREE.MeshStandardMaterial({
    vertexColors: true,
    map: DRY_COLOR,
    normalMap: DRY_NORMAL,
    roughnessMap: DRY_ROUGHNESS,
    roughness: 1,
    metalness: 0,
    normalScale: new THREE.Vector2(0.15, 0.15),
  }),
} as const;

const PALETTE = {
  soil: new THREE.Color(0x463a23),
  scrub: new THREE.Color(0x5a5a2f),
  grass: new THREE.Color(0x59683b),
  lush: new THREE.Color(0x4e6640),
  rock: new THREE.Color(0x6b6252),
  sand: new THREE.Color(0x8b7957),
  urbanDust: new THREE.Color(0x6f6654),
  field: new THREE.Color(0x746342),
  roadDust: new THREE.Color(0x9a8a70),
  dryWash: new THREE.Color(0x91816a),
  roadCore: new THREE.Color(0x6f6554),
  furrow: new THREE.Color(0x65563a),
} as const;

function smoothNoise(x: number, z: number, scale: number, seed: number): number {
  const sx = x * scale + seed * 13.1;
  const sz = z * scale + seed * 7.3;
  return (
    Math.sin(sx) * 0.45 +
    Math.cos(sz * 1.13) * 0.35 +
    Math.sin((sx + sz) * 0.6) * 0.2
  ) * 0.5 + 0.5;
}

function fbm(x: number, z: number): number {
  let value = 0;
  let amp = 0.55;
  let scale = 0.0042;
  for (let i = 0; i < 4; i++) {
    value += smoothNoise(x, z, scale, i + 1) * amp;
    scale *= 2.05;
    amp *= 0.52;
  }
  return THREE.MathUtils.clamp(value, 0, 1);
}

function distanceToSegment2D(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  const cx = ax + abx * t;
  const cz = az + abz * t;
  return Math.hypot(px - cx, pz - cz);
}

function roadMaskAt(x: number, z: number): { core: number; shoulder: number } {
  let core = 0;
  let shoulder = 0;
  for (const road of ROAD_LAYOUTS) {
    for (let i = 0; i < road.points.length - 1; i++) {
      const a = road.points[i]!;
      const b = road.points[i + 1]!;
      const distance = distanceToSegment2D(x, z, a.x, a.z, b.x, b.z);
      const coreRadius = road.width * 0.6;
      const shoulderRadius = road.width * 1.45;
      if (distance <= shoulderRadius) {
        shoulder = Math.max(shoulder, 1 - distance / shoulderRadius);
      }
      if (distance <= coreRadius) {
        core = Math.max(core, 1 - distance / coreRadius);
      }
    }
  }
  return { core, shoulder };
}

function fieldMaskAt(x: number, z: number): { fill: number; stripe: number } {
  let fill = 0;
  let stripe = 0;
  for (const field of FIELD_LAYOUTS) {
    const cos = Math.cos(field.rotation);
    const sin = Math.sin(field.rotation);
    const lx = (x - field.x) * cos + (z - field.z) * sin;
    const lz = -(x - field.x) * sin + (z - field.z) * cos;
    const hx = field.width * 0.5;
    const hz = field.depth * 0.5;
    if (Math.abs(lx) > hx || Math.abs(lz) > hz) continue;
    const edge = Math.max(Math.abs(lx) / hx, Math.abs(lz) / hz);
    const body = 1 - edge;
    fill = Math.max(fill, body);
    const furrow = Math.sin((lx / Math.max(12, field.width * 0.12)) * Math.PI);
    stripe = Math.max(stripe, body * (0.5 + furrow * 0.5));
  }
  return { fill, stripe };
}

function settlementMaskAt(x: number, z: number): number {
  const dist = Math.hypot(x, z);
  const urban = 1 - THREE.MathUtils.smoothstep(dist, 150, 280);
  const suburban = THREE.MathUtils.smoothstep(dist, 170, 250) * (1 - THREE.MathUtils.smoothstep(dist, 420, 640));
  return Math.max(urban, suburban * 0.65);
}

export function paintGroundVertexColors(geo: THREE.PlaneGeometry, size: number): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const norm = geo.attributes["normal"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const splats = new Float32Array(pos.count * 3);
  const tmp = new THREE.Color();
  const slopeNormal = new THREE.Vector3();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const dist = Math.hypot(x, z) / (size * 0.5);
    slopeNormal.set(norm.getX(i), norm.getY(i), norm.getZ(i));
    const slope = 1 - slopeNormal.dot(new THREE.Vector3(0, 1, 0));

    const macro = fbm(x, z);
    const micro = smoothNoise(x, z, 0.026, 7);
    const roadMask = roadMaskAt(x, z);
    const fieldMask = fieldMaskAt(x, z);
    const settlementMask = settlementMaskAt(x, z);
    const basin = THREE.MathUtils.clamp(1 - Math.abs(y) / 8.5, 0, 1);
    const farDry = THREE.MathUtils.smoothstep(dist, 0.28, 0.95);
    const dryBraid = smoothNoise(x, z, 0.01, 11);

    tmp.copy(PALETTE.grass).lerp(PALETTE.scrub, farDry * 0.8);
    tmp.lerp(PALETTE.lush, basin * 0.24 * (1 - farDry));
    tmp.lerp(PALETTE.soil, THREE.MathUtils.clamp((macro - 0.58) * 1.8, 0, 0.7));
    tmp.lerp(PALETTE.sand, THREE.MathUtils.clamp((0.26 - Math.abs(y)) * 1.1, 0, 0.45));
    tmp.lerp(PALETTE.rock, THREE.MathUtils.clamp((slope - 0.16) * 3.2, 0, 0.8));
    tmp.lerp(PALETTE.dryWash, THREE.MathUtils.clamp((micro - 0.7) * 0.5, 0, 0.18));
    tmp.lerp(PALETTE.scrub, Math.max(0, dryBraid - 0.68) * 0.12);

    // Vertex colors act as a tint over the splat-blended albedo. Lift the
    // natural-terrain tint toward unity so the textures carry brightness —
    // the raw palette values are authored dark and were a large part of the
    // old murk…
    tmp.multiplyScalar(1.8);

    // …but apply the man-made overlays (fields, settlement dust, roads)
    // AFTER the lift, with the original dark palette values, so worn ground
    // keeps its contrast against the lifted grassland instead of washing out.
    tmp.lerp(PALETTE.urbanDust, settlementMask * 0.55);
    tmp.lerp(PALETTE.field, fieldMask.fill * 0.55);
    tmp.lerp(PALETTE.furrow, fieldMask.stripe * 0.2);
    tmp.lerp(PALETTE.roadDust, roadMask.shoulder * 0.5);
    tmp.lerp(PALETTE.roadCore, roadMask.core * 0.55);

    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;

    // Splat weights: x=grass, y=rock, z=dirt (normalized in the shader).
    // Roads, fields, and settlements read as worn dirt; slopes and rocky
    // macro-noise expose rock; everything else is grassland drying with
    // distance from the map center.
    const worn = Math.max(roadMask.shoulder, fieldMask.fill * 0.9, settlementMask);
    const grassW = Math.max(0, (1 - slope * 3.4)) * (1 - farDry * 0.6) * (1 - worn) * 0.85;
    const rockW = THREE.MathUtils.clamp((slope - 0.1) * 4.2, 0, 1) + Math.max(0, macro - 0.62) * 0.9;
    const dirtW = 0.35 + worn * 1.7 + farDry * 0.55;
    splats[i * 3] = grassW;
    splats[i * 3 + 1] = rockW;
    splats[i * 3 + 2] = dirtW;
  }

  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.setAttribute("splat", new THREE.BufferAttribute(splats, 3));
}

export type FarFieldPalette = { inner: number; outer: number; dry: number };

export function paintFarFieldVertexColors(
  geo: THREE.CircleGeometry,
  innerRadius: number,
  // Default palette suits map01's grassland; arid maps pass their own so the
  // disc doesn't read as a pale sea behind the terrain.
  palette: FarFieldPalette = { inner: 0x6e745e, outer: 0x928b74, dry: 0x84785e },
): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const radius = geo.parameters.radius;
  const colorInner = new THREE.Color(palette.inner);
  const colorOuter = new THREE.Color(palette.outer);
  const dry = new THREE.Color(palette.dry);
  const tmp = new THREE.Color();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const dist = Math.hypot(x, z);
    const t = THREE.MathUtils.clamp((dist - innerRadius) / (radius - innerRadius), 0, 1);
    const warp = fbm(x * 0.55, z * 0.55);

    pos.setY(i, -2 - t * 18);
    tmp.copy(colorInner).lerp(colorOuter, t);
    tmp.lerp(dry, Math.max(0, warp - 0.52) * 0.55);
    tmp.multiplyScalar(1.7); // tint over texture, same lift rationale as the ground
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }

  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
}
